import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteLeadSource,
    getLeadSources,
    updateLeadSourceStatus,
} from '../store/leadSources/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const LeadSourcesContext = createContext();

export const useLeadSources = () => useContext(LeadSourcesContext);

export const LeadSourcesProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, LeadSourcesList, loading, errors } = useSelector((state) => state.LeadSources);

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });

    const debouncedGetLeadSources = useMemo(() => debounce((params) => {
        dispatch(getLeadSources(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetLeadSources({
            currentPage: 1,
            perPage: LeadSourcesList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, LeadSourcesList?.pagination?.perPage, debouncedGetLeadSources]);

    const leadSources = useMemo(() => LeadSourcesList?.data?.map(
        ({ _id, key, title, status }, index) => ({
            id: index + 1,
            _id,
            key,
            title,
            status: capitalizeFirst(status),
        })
    ), [LeadSourcesList]);

    const pagination = useMemo(() => LeadSourcesList?.pagination, [LeadSourcesList]);
        
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetLeadSources({ currentPage, perPage, ...filters });
    }, [debouncedGetLeadSources, filters]);

    const editLeadSource = useCallback((data) => setUpdateData(data), []);

    const handleCloseModal = useCallback((action) => {
        if(action === "update"){
            setUpdateData({id:"", title:""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }, []);

    const handleToggleStatus = useCallback((id) => dispatch(updateLeadSourceStatus(id)), [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteLeadSource(toDeleteId));
        handleCloseModal('delete');
    }, [dispatch, toDeleteId, handleCloseModal]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [LeadSourcesList, handleCloseModal]);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('leadSources.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('leadSources.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => editLeadSource(info.row.original)} />
                    )}
                    {hasPermission('leadSources.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, editLeadSource, askDeleteConfirmation]);

    const table = useReactTable({
        data: leadSources,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: LeadSourcesList?.pagination?.currentPage - 1,
                pageSize: LeadSourcesList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: LeadSourcesList?.pagination?.totalPages,
    });

    return (
        <LeadSourcesContext.Provider value={{
            leadSources,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editLeadSource,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </LeadSourcesContext.Provider>
    );
};
